/**
 * Header banner style presets (frontend).
 */

export const FONT_FAMILY_OPTIONS = [
  { value: "default", label: "Default (by type)" },
  { value: "system", label: "System UI" },
  { value: "sans", label: "Sans-serif" },
  { value: "serif", label: "Serif" },
  { value: "mono", label: "Monospace" },
  { value: "arial", label: "Arial" },
  { value: "georgia", label: "Georgia" },
  { value: "times", label: "Times New Roman" },
  { value: "courier", label: "Courier New" },
  { value: "dm-sans", label: "DM Sans" },
];

export const TEXT_ALIGN_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

export const FONT_SIZE_OPTIONS = [
  { value: "", label: "Default (14px)" },
  { value: "12", label: "12 px" },
  { value: "14", label: "14 px" },
  { value: "16", label: "16 px" },
  { value: "18", label: "18 px" },
  { value: "20", label: "20 px" },
  { value: "24", label: "24 px" },
  { value: "28", label: "28 px" },
  { value: "32", label: "32 px" },
];

export const BAR_COLOR_PRESETS = [
  { value: "", label: "Default (by type)", color: null },
  { value: "#4318FF", label: "Brand blue", color: "#4318FF" },
  { value: "#F59E0B", label: "Amber", color: "#F59E0B" },
  { value: "#0284C7", label: "Sky blue", color: "#0284C7" },
  { value: "#10B981", label: "Green", color: "#10B981" },
  { value: "#EF4444", label: "Red", color: "#EF4444" },
  { value: "#7C3AED", label: "Purple", color: "#7C3AED" },
  { value: "#111827", label: "Dark gray", color: "#111827" },
  { value: "#FFFFFF", label: "White", color: "#FFFFFF" },
];

export const TEXT_COLOR_PRESETS = [
  { value: "", label: "Default (by type)", color: null },
  { value: "#FFFFFF", label: "White", color: "#FFFFFF" },
  { value: "#111827", label: "Dark", color: "#111827" },
  { value: "#FEF3C7", label: "Cream", color: "#FEF3C7" },
  { value: "#DBEAFE", label: "Light blue", color: "#DBEAFE" },
];

const FONT_STACKS = {
  system:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  serif: 'ui-serif, Georgia, "Times New Roman", Times, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace',
  arial: 'Arial, Helvetica, sans-serif',
  georgia: "Georgia, serif",
  times: '"Times New Roman", Times, serif',
  courier: '"Courier New", Courier, monospace',
  "dm-sans": '"DM Sans", ui-sans-serif, system-ui, sans-serif',
};

const KIND_DEFAULTS = {
  promotion: { bar: "#4318FF", text: "#FFFFFF" },
  maintenance: { bar: "#F59E0B", text: "#451A03" },
  update: { bar: "#0284C7", text: "#FFFFFF" },
  info: { bar: "rgba(255,255,255,0.1)", text: "#FFFFFF" },
};

function isValidHex(v) {
  return /^#[0-9A-Fa-f]{6}$/.test(String(v || ""));
}

export const EMPTY_STYLE_FORM = {
  barColor: "",
  textColor: "",
  fontFamily: "default",
  fontSizePx: "",
  textAlign: "center",
  fontItalic: false,
};

export function styleFormFromRow(row) {
  return {
    barColor: row?.barColor || "",
    textColor: row?.textColor || "",
    fontFamily: row?.fontFamily || "default",
    fontSizePx: row?.fontSizePx != null ? String(row.fontSizePx) : "",
    textAlign: row?.textAlign || "center",
    fontItalic: Boolean(row?.fontItalic),
  };
}

export function stylePayloadFromForm(style) {
  return {
    barColor: style.barColor?.trim() || null,
    textColor: style.textColor?.trim() || null,
    fontFamily: style.fontFamily === "default" ? null : style.fontFamily,
    fontSizePx: style.fontSizePx ? Number(style.fontSizePx) : null,
    textAlign: style.textAlign || "center",
    fontItalic: Boolean(style.fontItalic),
  };
}

export function resolveBannerPresentation(row) {
  const kind = String(row?.kind || "info").toLowerCase();
  const defaults = KIND_DEFAULTS[kind] || KIND_DEFAULTS.info;

  const barColor = isValidHex(row?.barColor) ? row.barColor : null;
  const textColor = isValidHex(row?.textColor) ? row.textColor : null;
  const align = ["left", "center", "right"].includes(row?.textAlign)
    ? row.textAlign
    : "center";

  const barStyle = {};
  if (barColor) {
    barStyle.backgroundColor = barColor;
  } else {
    barStyle.backgroundColor = defaults.bar;
  }

  const textStyle = {
    color: textColor || defaults.text,
    fontSize: row?.fontSizePx ? `${row.fontSizePx}px` : "14px",
    fontStyle: row?.fontItalic ? "italic" : "normal",
  };

  const fontKey = row?.fontFamily;
  if (fontKey && FONT_STACKS[fontKey]) {
    textStyle.fontFamily = FONT_STACKS[fontKey];
  }

  const flexJustify =
    align === "left"
      ? "flex-start"
      : align === "right"
        ? "flex-end"
        : "center";

  return {
    barStyle,
    textStyle,
    textAlign: align,
    flexJustify,
    useKindClass: !barColor && kind === "info",
  };
}
